import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { hrAPI } from '../lib/api';
import { useHrPermissions } from '../hooks/useHrPermissions';
import { Users, Loader2, Plus, Search, Shield, ChevronRight, UserCircle, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const EMPLOYMENT_BADGES = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  on_leave: 'bg-amber-100 text-amber-700 border-amber-200',
  terminated: 'bg-slate-100 text-slate-500 border-slate-200',
};

export const HrDirectory = () => {
  const navigate = useNavigate();
  const { perms, loading: permLoading } = useHrPermissions();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [includeTerminated, setIncludeTerminated] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ first_name: '', last_name: '', job_title: '', work_email: '' });
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const r = await hrAPI.listEmployees(includeTerminated);
      setEmployees(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (perms) fetchAll();
    // eslint-disable-next-line
  }, [perms, includeTerminated]);

  const handleCreate = async () => {
    if (!draft.first_name.trim() || !draft.last_name.trim()) {
      toast.error('First and last name required');
      return;
    }
    setSaving(true);
    try {
      const r = await hrAPI.createEmployee(draft);
      toast.success(`Created ${r.data.first_name} ${r.data.last_name}`);
      setCreateOpen(false);
      setDraft({ first_name: '', last_name: '', job_title: '', work_email: '' });
      fetchAll();
      navigate(`/hr/employees/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create');
    } finally {
      setSaving(false);
    }
  };

  if (permLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      </Layout>
    );
  }

  if (!perms?.has_hr_access && !perms?.is_hr_admin) {
    // Self-service only — redirect to /hr/me
    return (
      <Layout>
        <div className="max-w-md mx-auto py-12 text-center">
          <UserCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">My HR Profile</h2>
          <p className="text-sm text-slate-500 mt-1 mb-4">Edit your contact details, address, and emergency contact information.</p>
          <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="go-to-my-profile">
            <Link to="/hr/me">Open My Profile <ChevronRight className="w-4 h-4 ml-1" /></Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const filtered = employees.filter(e => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (`${e.first_name || ''} ${e.last_name || ''}`).toLowerCase().includes(q)
      || (e.job_title || '').toLowerCase().includes(q)
      || (e.work_email || '').toLowerCase().includes(q);
  });

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="hr-directory-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-3">
              <Users className="w-7 h-7 text-blue-600" /> HR Directory
            </h1>
            <p className="text-slate-500 mt-1">
              {perms.is_hr_admin ? 'Full HR Admin access' : perms.is_hr_viewer ? 'Read-only HR access' : 'Direct reports only'}
              {' · '}{employees.length} employee{employees.length === 1 ? '' : 's'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline" data-testid="my-profile-btn">
              <Link to="/hr/me"><UserCircle className="w-4 h-4 mr-1" /> My Profile</Link>
            </Button>
            {perms.is_hr_admin && (
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setCreateOpen(true)} data-testid="add-employee-btn">
                <Plus className="w-4 h-4 mr-1" /> New Employee
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search name, job title, email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="hr-search"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={includeTerminated}
              onChange={(e) => setIncludeTerminated(e.target.checked)}
              data-testid="show-terminated"
            />
            Show terminated
          </label>
          {!perms.is_hr_admin && (
            <Badge className="bg-amber-100 text-amber-700 border border-amber-200">
              <Shield className="w-3 h-3 mr-1" /> Salary & bank details hidden
            </Badge>
          )}
        </div>

        {/* Directory table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-slate-400 py-12 text-sm">
                {search ? 'No employees match the search.' : 'No employees yet. Click "New Employee" to create one.'}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Job Title</TableHead>
                    <TableHead>Work Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Linked Engineer</TableHead>
                    <TableHead className="text-right">Open</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(e => (
                    <TableRow
                      key={e.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => navigate(`/hr/employees/${e.id}`)}
                      data-testid={`employee-row-${e.id}`}
                    >
                      <TableCell>
                        <p className="text-sm font-semibold text-slate-900">{e.first_name} {e.last_name}</p>
                        {e.start_date && <p className="text-[11px] text-slate-500">Joined {new Date(e.start_date).toLocaleDateString()}</p>}
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">{e.job_title || '—'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{e.work_email || '—'}</TableCell>
                      <TableCell>
                        <Badge className={`${EMPLOYMENT_BADGES[e.employment_status] || EMPLOYMENT_BADGES.active} border text-[10px]`}>
                          {(e.employment_status || 'active').replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {e.engineer_id ? (
                          <Badge className="bg-blue-50 text-blue-700 border-blue-200 border text-[10px]">
                            <Briefcase className="w-3 h-3 mr-1" /> Linked
                          </Badge>
                        ) : (
                          <span className="text-[11px] text-slate-400">Not linked</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <ChevronRight className="w-4 h-4 text-slate-400 inline" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-md" data-testid="employee-create-dialog">
            <DialogHeader>
              <DialogTitle>New Employee</DialogTitle>
              <DialogDescription>Enter the basics now; you can fill in the rest from the profile page.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">First Name *</Label>
                <Input value={draft.first_name} onChange={(e) => setDraft({ ...draft, first_name: e.target.value })} data-testid="emp-first" />
              </div>
              <div>
                <Label className="text-xs">Last Name *</Label>
                <Input value={draft.last_name} onChange={(e) => setDraft({ ...draft, last_name: e.target.value })} data-testid="emp-last" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Job Title</Label>
                <Input value={draft.job_title} onChange={(e) => setDraft({ ...draft, job_title: e.target.value })} placeholder="e.g. Lead Installer" data-testid="emp-title" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Work Email</Label>
                <Input type="email" value={draft.work_email} onChange={(e) => setDraft({ ...draft, work_email: e.target.value })} placeholder="alex@helioses.co.uk" data-testid="emp-email" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="emp-create">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default HrDirectory;
